<template>
  <el-container class="suggestion-page">
    <el-header height="60px" class="header">
      <div class="title-wrap">
        <span class="title">投诉投诉</span>
        <span class="desc">提交产品投诉或问题反馈，管理员处理后会在这里展示回复结果。</span>
      </div>
      <el-button type="primary" icon="el-icon-edit" @click="handleOpenSubmit">提交投诉</el-button>
    </el-header>
    <el-main class="main">
      <el-tabs v-model="activeStatus" @tab-click="handleTabChange">
        <el-tab-pane label="处理中" name="1"></el-tab-pane>
        <el-tab-pane label="已回复" name="3"></el-tab-pane>
      </el-tabs>

      <el-empty v-if="suggestionList.length === 0" description="暂无投诉记录"></el-empty>
      <div v-else class="list">
        <div
          v-for="item in suggestionList"
          :key="item.id"
          :ref="`suggestion-${item.id}`"
          class="card"
          :class="{ highlighted: isHighlighted(item) }"
          @click="handleOpenDetail(item)"
        >
          <div v-if="isHighlighted(item)" class="highlight-tip">
            <i class="el-icon-bell"></i>
            <span>来自系统通知的投诉消息，请点击查看详情</span>
          </div>
          <div class="card-header">
            <div class="card-title">{{ item.title }}</div>
            <el-tag :type="getStatusTag(item.status)" size="mini">{{ getStatusText(item.status) }}</el-tag>
          </div>
          <div class="meta">{{ getTypeText(item.type) }} · {{ item.createTime }}</div>
          <div class="content">{{ item.content }}</div>
          <div v-if="item.replyContent" class="reply">管理员回复：{{ item.replyContent }}</div>
        </div>
      </div>
      <div v-if="!noMore" class="load-more" @click="handleLoadMore">加载更多</div>
    </el-main>

    <el-dialog v-dialogDrag title="提交投诉" :visible.sync="submitVisible" width="650px">
      <el-form ref="submitFormRef" :model="formData" :rules="rules" label-width="90px">
        <el-form-item label="投诉类型" prop="type">
          <el-select v-model="formData.type">
            <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="formData.title" maxlength="100" show-word-limit placeholder="请简要描述你的投诉" />
        </el-form-item>
        <el-form-item label="投诉内容" prop="content">
          <el-input v-model="formData.content" type="textarea" :rows="6" maxlength="1000" show-word-limit placeholder="请详细说明你的问题或投诉" />
        </el-form-item>
        <el-form-item label="联系方式">
          <el-input v-model="formData.contact" maxlength="100" placeholder="选填，便于管理员联系你" />
        </el-form-item>
        <el-form-item label="图片附件">
          <div class="images">
            <div v-for="(img, idx) in formData.images" :key="img.id" class="image-item" v-loading="img.loading">
              <img class="image" :src="img.thumbUrl" loading="lazy" />
              <div class="mask">
                <i class="icon el-icon-zoom-in" @click="handlePreviewImage(idx)" />
                <i class="icon el-icon-delete" @click="handleRemoveImage(idx)" />
              </div>
            </div>
            <file-upload
              v-show="formData.images.length < maxUploadSize"
              :action="'/image/upload'"
              :maxSize="10 * 1024 * 1024"
              :fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif']"
              @before="handleUploadBefore"
              @success="handleUploadSuccess"
              @fail="handleUploadFail"
            >
              <div class="upload-image"><i class="el-icon-plus"></i></div>
            </file-upload>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="submitVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">提交</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog v-dialogDrag title="投诉详情" :visible.sync="detailVisible" width="650px">
      <div class="detail">
        <h3>{{ detail.title }}</h3>
        <p class="meta">{{ getTypeText(detail.type) }} · {{ detail.createTime }}</p>
        <p class="detail-content">{{ detail.content }}</p>
        <div v-if="detail.images && detail.images.length" class="detail-images">
          <img v-for="(img, idx) in detail.images" :key="idx" :src="img.thumbUrl" @click="handlePreviewDetailImage(idx)" />
        </div>
        <el-divider />
        <h4>处理结果</h4>
        <p>{{ getStatusText(detail.status) }}</p>
        <p v-if="detail.replyTime" class="meta">回复时间：{{ detail.replyTime }}</p>
        <p v-if="detail.replyContent" class="reply-content">{{ detail.replyContent }}</p>
        <el-empty v-else description="管理员暂未回复，请耐心等待" :image-size="80"></el-empty>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
import FileUpload from '../common/FileUpload.vue';

export default {
  name: 'Suggestion',
  components: { FileUpload },
  data() {
    return {
      activeStatus: '1',
      suggestionList: [],
      noMore: false,
      submitVisible: false,
      detailVisible: false,
      detail: {},
      maxImageId: 1,
      maxUploadSize: 9,
      highlightId: '',
      highlightedDetail: null,
      formData: { type: 1, title: '', content: '', contact: '', images: [] },
      typeOptions: [
        { value: 1, label: '服务问题' },
        { value: 2, label: '课程问题' },
      ]
    };
  },
  computed: {
    rules() {
      return {
        type: [{ required: true, message: '请选择投诉类型', trigger: 'change' }],
        title: [{ required: true, message: '请输入投诉标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入投诉内容', trigger: 'blur' }]
      };
    },
    nextPageMaxId() {
      return this.suggestionList.length ? this.suggestionList[this.suggestionList.length - 1].id : -1;
    }
  },
  methods: {
    init() {
      this.highlightId = this.getRouteSuggestionId();
      if (this.highlightId) {
        this.loadHighlightedSuggestion(this.highlightId);
        return;
      }
      this.loadList();
    },
    getRouteSuggestionId() {
      return this.$route.query.id || this.$route.query.suggestionId || this.$route.query.messageId || '';
    },
    handleTabChange() {
      this.highlightId = '';
      this.highlightedDetail = null;
      this.loadList();
    },
    loadList() {
      this.suggestionList = [];
      this.noMore = false;
      this.handleLoadMore();
    },
    handleLoadMore() {
      this.$http({
        url: `/suggestion/page?maxPageId=${this.nextPageMaxId}&status=${this.activeStatus}`,
        method: 'GET'
      }).then((res) => {
        const list = Array.isArray(res) ? res : [];
        this.appendSuggestions(list);
        this.ensureHighlightedSuggestionVisible();
        this.noMore = list.length < 10;
        this.$nextTick(() => this.scrollToHighlighted());
      });
    },
    appendSuggestions(list) {
      const existsIds = new Set(this.suggestionList.map(item => String(item.id)));
      const newItems = list.filter(item => {
        const id = String(item.id);
        if (existsIds.has(id)) return false;
        existsIds.add(id);
        return true;
      });
      this.suggestionList.push(...newItems);
    },
    loadHighlightedSuggestion(id) {
      this.$http({
        url: `/suggestion/info?id=${id}`,
        method: 'GET'
      }).then((res) => {
        this.highlightedDetail = res;
        this.activeStatus = this.getSuggestionTabStatus(res.status);
        this.loadList();
      }).catch(() => {
        this.highlightId = '';
        this.loadList();
      });
    },
    handleOpenSubmit() {
      this.formData = { type: 1, title: '', content: '', contact: '', images: [] };
      this.submitVisible = true;
    },
    handleSubmit() {
      this.$refs.submitFormRef.validate((valid) => {
        if (!valid) return;
        if (this.formData.images.some(item => item.loading)) {
          this.$message.warning('请等待图片上传完成');
          return;
        }
        this.$http({
          url: '/suggestion/submit',
          method: 'post',
          data: this.formData
        }).then(() => {
          this.$message.success('提交成功');
          this.submitVisible = false;
          this.activeStatus = '1';
          this.loadList();
        });
      });
    },
    handleOpenDetail(item) {
      this.openDetailById(item.id);
    },
    openDetailById(id) {
      this.$http({
        url: `/suggestion/info?id=${id}`,
        method: 'GET'
      }).then((res) => {
        this.detail = res;
        this.detailVisible = true;
      });
    },
    isHighlighted(item) {
      return this.highlightId && String(item.id) === String(this.highlightId);
    },
    ensureHighlightedSuggestionVisible() {
      if (!this.highlightedDetail || this.suggestionList.some(item => this.isHighlighted(item))) return;
      this.suggestionList.unshift(this.highlightedDetail);
    },
    scrollToHighlighted() {
      if (!this.highlightId) return;
      const refs = this.$refs[`suggestion-${this.highlightId}`];
      const target = Array.isArray(refs) ? refs[0] : refs;
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    getSuggestionTabStatus(status) {
      return Number(status) === 3 ? '3' : '1';
    },
    getTypeText(type) {
      return this.typeOptions.find(item => item.value === type)?.label || '-';
    },
    getStatusText(status) {
      if (status === 1) return '待处理';
      if (status === 2) return '处理中';
      if (status === 3) return '已回复';
      return '已关闭';
    },
    getStatusTag(status) {
      if (status === 1) return 'warning';
      if (status === 2) return 'primary';
      if (status === 3) return 'success';
      return 'info';
    },
    handleUploadBefore(file) {
      if (this.formData.images.length >= this.maxUploadSize) {
        return false;
      }
      const id = this.maxImageId++;
      file.uid = id;
      const url = URL.createObjectURL(file);
      this.formData.images.push({ id, thumbUrl: url, originUrl: url, loading: true });
      return true;
    },
    handleUploadSuccess(file, res) {
      const image = this.formData.images.find(item => item.id === file.uid);
      if (image) {
        Object.assign(image, res.data, { loading: false });
      }
    },
    handleUploadFail(file) {
      const idx = this.formData.images.findIndex(item => item.id === file.uid);
      if (idx >= 0) {
        this.formData.images.splice(idx, 1);
      }
    },
    handleRemoveImage(idx) {
      this.formData.images.splice(idx, 1);
    },
    handlePreviewImage(idx) {
      this.previewImages(this.formData.images, idx);
    },
    handlePreviewDetailImage(idx) {
      this.previewImages(this.detail.images, idx);
    },
    previewImages(images, idx) {
      this.$eventBus.$emit('openFullImage', images[idx].originUrl || images[idx].thumbUrl);
    }
  },
  mounted() {
    this.init();
  }
};
</script>

<style lang="scss" scoped>
.suggestion-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f6fa;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .title-wrap {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .title {
        font-size: 18px;
        font-weight: 600;
        color: var(--im-text-color);
      }

      .desc {
        font-size: 13px;
        color: var(--im-text-color-light);
      }
    }
  }

  .main {
    padding: 16px 20px;

    .list {
      display: grid;
      gap: 12px;
    }

    .card {
      background: #fff;
      border-radius: 12px;
      padding: 16px 18px;
      cursor: pointer;
      border: 1px solid rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
        transform: translateY(-1px);
      }

      &.highlighted {
        border-color: var(--im-color-primary);
        background: linear-gradient(180deg, rgba(64, 158, 255, 0.12), #fff 48%);
        box-shadow: 0 10px 28px rgba(64, 158, 255, 0.18);
        animation: suggestion-highlight-pulse 1.6s ease-in-out 2;
      }

      .highlight-tip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 12px;
        padding: 6px 10px;
        border-radius: 999px;
        background: rgba(64, 158, 255, 0.12);
        color: var(--im-color-primary);
        font-size: 13px;
        font-weight: 500;
      }

      .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;

        .card-title {
          font-weight: 600;
          color: var(--im-text-color);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .meta {
        margin-top: 8px;
        font-size: 13px;
        color: var(--im-text-color-light);
      }

      .content {
        margin-top: 10px;
        line-height: 1.6;
        color: var(--im-text-color);
        white-space: pre-wrap;
        word-break: break-all;
      }

      .reply {
        margin-top: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        background: #f7f8fc;
        color: var(--im-text-color);
      }
    }

    .load-more {
      text-align: center;
      color: var(--im-color-primary);
      cursor: pointer;
      padding: 16px 0 8px;
      user-select: none;
    }
  }

  .images {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .image-item,
    .upload-image {
      width: 90px;
      height: 90px;
      border-radius: 8px;
      overflow: hidden;
      border: 1px dashed #ddd;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      .image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .mask {
        display: none;
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        align-items: center;
        justify-content: center;
        gap: 10px;
        color: #fff;
      }

      &:hover .mask {
        display: flex;
      }

      .icon {
        cursor: pointer;
        font-size: 18px;
      }
    }
  }

  .detail-content,
  .reply-content {
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.6;
  }

  .detail-images {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    img {
      width: 90px;
      height: 90px;
      object-fit: cover;
      border-radius: 8px;
      cursor: pointer;
    }
  }
}

@keyframes suggestion-highlight-pulse {
  0%,
  100% {
    box-shadow: 0 10px 28px rgba(64, 158, 255, 0.18);
  }

  50% {
    box-shadow: 0 12px 34px rgba(64, 158, 255, 0.34);
  }
}
</style>
